/**
 * The Home dashboard's three columns (Cash, Expenses, Revenue), shared by the
 * customer app and the admin console so the two Homes are one design.
 *
 * No cards: the columns are open, separated by a hairline on a wide screen
 * and stacked with a rule between them on a phone. Each column is one
 * hierarchy, top to bottom: the title row (with the report link), the big
 * figure, its delta, the supporting rows, then titled sections (a chart, a
 * breakdown). Nothing stretches to the tallest column, so an empty column
 * ends where its content ends instead of trailing white space.
 *
 * A section with no data draws its message in the chart's own height, so a
 * period with no expenses is the same geometry as one with many.
 */
import type { CSSProperties, ReactNode } from "react";

import { cn } from "./cn";
import { Skeleton } from "./skeleton";
import { focusRing, touchHitArea } from "./recipes";

/**
 * One column's box, exported for a skeleton that must keep the exact geometry
 * (the phone stack rule included: a rule and padding above every column but
 * the first, gone on `lg` where the hairline between columns takes over).
 */
export const dashboardColumnClass =
  "flex min-w-0 flex-col gap-5 not-first:border-t not-first:border-border not-first:pt-6 lg:not-first:border-t-0 lg:not-first:pt-0 lg:px-6 lg:first:pl-0 lg:last:pr-0";

export function DashboardColumns({
  children,
  className,
  divided = true,
  busy = false,
}: {
  children: ReactNode;
  className?: string;
  /** False when a notice spans the other columns: no hairline next to prose. */
  divided?: boolean;
  /** A newer period is loading: the figures stay, dimmed, instead of a blank pane. */
  busy?: boolean;
}) {
  return (
    <div
      data-slot="dashboard-columns"
      data-divided={divided ? "" : undefined}
      aria-busy={busy || undefined}
      className={cn(
        "grid grid-cols-1 gap-6 transition-opacity lg:grid-cols-3 lg:gap-0",
        divided && "lg:divide-x lg:divide-border",
        busy && "opacity-60",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function DashboardColumn({
  title,
  action,
  children,
  testId,
}: {
  title: string;
  /** The report link at the title's right: the app's own Link, in `dashboardLinkClass`. */
  action?: ReactNode;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <section
      data-slot="dashboard-column"
      className={dashboardColumnClass}
      aria-label={title}
      data-testid={testId ?? `dashboard-${title.toLowerCase()}`}
    >
      <div className="flex h-7 items-center justify-between gap-2">
        <h3 className="text-lg leading-7 font-semibold tracking-[-0.01em] text-foreground">
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

/** The report link's look: a quiet arrow that darkens on hover, 44px to a finger. */
export const dashboardLinkClass = `relative inline-flex size-7 shrink-0 items-center justify-center rounded-control text-muted-foreground transition-colors hover:bg-muted hover:text-foreground ${focusRing} ${touchHitArea}`;

/** The arrow inside the report link. */
export function DashboardLinkArrow() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      className="size-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 11l6-6M6 5h5v5" />
    </svg>
  );
}

/**
 * A titled block inside a column: "Net cash flow trend" over a chart, "By
 * ledger account" over a breakdown. With `empty` set it draws that message
 * centred in `height` instead of the children, so the column keeps its
 * geometry; a second line (`emptyHint`) says what will fill it.
 */
export function DashboardSection({
  title,
  legend,
  empty,
  emptyHint,
  height = 180,
  children,
  className,
}: {
  title: string;
  legend?: ReactNode;
  empty?: string | null;
  emptyHint?: string;
  height?: number;
  /** Absent for a section that only ever shows its empty message. */
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      data-slot="dashboard-section"
      className={cn("flex flex-col gap-2", className)}
    >
      <p className="type-small text-muted-foreground">{title}</p>
      {empty ? null : legend}
      {empty ? (
        <div
          data-slot="dashboard-section-empty"
          className="flex min-h-(--section-height) flex-col items-center justify-center gap-1 rounded-control bg-surface px-4 text-center"
          style={{ "--section-height": `${height}px` } as CSSProperties}
        >
          <p className="type-small text-foreground">{empty}</p>
          {emptyHint ? (
            <p className="text-compact text-muted-foreground">{emptyHint}</p>
          ) : null}
        </div>
      ) : (
        children
      )}
    </div>
  );
}

/**
 * The columns' final geometry, drawn while the read is in flight: Cash with
 * its trend (180) and its cash-in-vs-out block (200), Expenses one section,
 * Revenue its trend and its breakdown (180 each) — the sections each drawn
 * column reserves, so nothing moves when the figures land.
 */
export function DashboardColumnsSkeleton() {
  const sections: Record<number, number[]> = {
    0: [180, 200],
    1: [180],
    2: [180, 180],
  };
  return (
    <DashboardColumns>
      {[0, 1, 2].map((column) => (
        <div
          key={column}
          className={dashboardColumnClass}
          data-slot="dashboard-column-skeleton"
        >
          <Skeleton className="h-7 w-24" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-16 w-full" />
          {sections[column]!.map((height, index) => (
            <Skeleton
              key={index}
              className={cn(
                "w-full",
                height === 200 ? "h-[200px]" : "h-[180px]",
              )}
            />
          ))}
        </div>
      ))}
    </DashboardColumns>
  );
}
